/* eslint-disable i18next/no-literal-string */
import { useState } from 'react';
import { useQuery } from 'react-query';
import { Box, Chip } from '@mui/material';
import { Select, PageHeader } from 'Components/ui';
import { useTranslation } from 'react-i18next';
import { adminGetFindings } from 'services/scanner-service';

const SEVERITY_COLORS = {
  critical: 'error',
  high: 'warning',
  medium: 'default',
  low: 'default',
  informational: 'default',
};
const SEVERITY_LABELS = {
  critical: 'Crítico',
  high: 'Alto',
  medium: 'Médio',
  low: 'Baixo',
  informational: 'Informacional',
};

export const AdminScannerFindings = () => {
  const { t } = useTranslation('scanner');
  const [page, setPage] = useState(1);
  const [severity, setSeverity] = useState('');
  const [status, setStatus] = useState('FAIL');

  const { data, isLoading } = useQuery(
    ['adminScannerFindings', page, severity, status],
    () => adminGetFindings(page, undefined, severity, status),
  );

  const findings = data?.data?.findings || [];
  const pagination = data?.data?.pagination || {};
  const totalPages = pagination.pages;

  return (
    <div className="mt-3 px-3">
      <PageHeader
        title={t('scanner:adminFindings.title')}
        segments={[
          { label: t('common:breadcrumbs.globalSettings') },
          { label: t('scanner:adminFindings.title') },
        ]}
      />

      <Box display="flex" gap={2} mb={3} flexWrap="wrap">
        <Select
          name="severity"
          value={severity}
          onChange={(value) => { setSeverity(value); setPage(1); }}
          fullWidth={false}
          sx={{ minWidth: 150 }}
          options={[
            { value: '', label: 'Selecione a severidade' },
            { value: 'all', label: 'Todas' },
            { value: 'critical', label: 'Crítico' },
            { value: 'high', label: 'Alto' },
            { value: 'medium', label: 'Médio' },
            { value: 'low', label: 'Baixo' },
            { value: 'informational', label: 'Informacional' },
          ]}
        />
        <Select
          name="status"
          value={status}
          onChange={(value) => { setStatus(value); setPage(1); }}
          fullWidth={false}
          sx={{ minWidth: 130 }}
          options={[
            { value: '', label: 'Selecione o status' },
            { value: 'all', label: 'Todos' },
            { value: 'FAIL', label: 'Falhou' },
            { value: 'PASS', label: 'Passou' },
            { value: 'MUTED', label: 'Ignorado' },
          ]}
        />
      </Box>

      {isLoading && <p>Carregando...</p>}

      {!isLoading && findings.length === 0 && (
        <p className="text-muted">Nenhum finding encontrado com os filtros selecionados.</p>
      )}

      {findings.length > 0 && (
        <Box sx={{ overflowX: 'auto' }}>
          <table className="table table-hover table-sm">
            <thead>
              <tr>
                <th>Check</th>
                <th>Serviço</th>
                <th>Recurso</th>
                <th>Região</th>
                <th>Severidade</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {findings.map((f) => (
                <tr key={f.id || f.uid}>
                  <td style={{ maxWidth: 280 }}>
                    <div style={{ fontWeight: 500, fontSize: 13 }}>
                      {f.check_title || f.check_id || '—'}
                    </div>
                    {f.check_title && (
                      <div style={{ fontSize: 11, color: 'var(--vo-steel)' }}>{f.check_id}</div>
                    )}
                    {f.status_extended && (
                      <div
                        style={{
                          fontSize: 11,
                          color: 'var(--vo-slate)',
                          marginTop: 2,
                          maxWidth: 260,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                        title={f.status_extended}
                      >
                        {f.status_extended}
                      </div>
                    )}
                  </td>
                  <td>
                    <Chip label={f.service_name || '—'} size="small" variant="outlined" sx={{ fontSize: 11 }} />
                  </td>
                  <td
                    style={{
                      maxWidth: 200,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontSize: 12,
                      fontFamily: 'monospace',
                    }}
                    title={f.resource_uid || f.resource_name}
                  >
                    {f.resource_uid || f.resource_name || '—'}
                  </td>
                  <td style={{ fontSize: 12 }}>{f.region || '—'}</td>
                  <td>
                    <Chip
                      label={SEVERITY_LABELS[f.severity] || f.severity || '—'}
                      color={SEVERITY_COLORS[f.severity] || 'default'}
                      size="small"
                    />
                  </td>
                  <td>
                    <Chip
                      label={f.status}
                      color={f.status === 'FAIL' ? 'error' : f.status === 'PASS' ? 'success' : 'default'}
                      size="small"
                      variant="outlined"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      )}

      <Box display="flex" gap={1} mt={2} alignItems="center" justifyContent="flex-end">
        <button className="btn btn-outline-secondary btn-sm" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
          Anterior
        </button>
        <span style={{ fontSize: 13, color: 'var(--vo-slate)' }}>
          Página {page}{totalPages ? ` de ${totalPages}` : ''}
        </span>
        <button
          className="btn btn-outline-secondary btn-sm"
          disabled={totalPages ? page >= totalPages : findings.length < 50}
          onClick={() => setPage((p) => p + 1)}
        >
          Próxima
        </button>
      </Box>
    </div>
  );
};
